import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { LoginPinFrequencyModel } from "@/Common/Models/Employee/LoginFrequencyModel";
import { Guid } from "@/Common/Models/Guid";
import { Person } from "@/Common/Models/Person/Person";
import { SitesModel } from "@/Common/Models/Sites/SitesModel";
import { PinFrequency } from "@/Common/Models/UI/Enums";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { BaseStore } from "@/Common/Stores/BaseStore";
import { RequestMapper, ServerResponseMapper } from "@/Common/Stores/Handlers/Server/RequestMapper";
import { EmailAddressesModel } from "../Models/EmailAddresses/EmailAddressesModel";
import { IConfigModel } from "../Models/UI/IConfigModel";
import { ISettingsUIModel } from "../Models/UI/ISettingsUIModel";
import { SettingsAction } from "./Actions/SettingsAction";
import { SettingsHandler } from "./Handlers/Local/SettingsHandler";
import { SettingsUIHandler } from "./Handlers/Local/SettingsUIHandler";
import { SettingsResponseHandler } from "./Handlers/Server/SettingsResponseHandler";

let defaultInstance: SettingsStore;

class SettingsStore extends BaseStore {
    private readonly mapper: RequestMapper;
    private readonly serverMapper: ServerResponseMapper;
    private readonly localHandler: SettingsHandler;
    private readonly uiHandler: SettingsUIHandler;
    private readonly responseHandler: SettingsResponseHandler;

    public readonly action: SettingsAction;

    public configuration: IConfigModel;
    public uiLogic: ISettingsUIModel;

    public constructor() {
        super();

        var self = this;
        this.mapper = new RequestMapper();
        this.serverMapper = new ServerResponseMapper();

        this.localHandler = new SettingsHandler();
        this.uiHandler = new SettingsUIHandler();
        this.mapper.AddInstance(this.localHandler);
        this.mapper.AddInstance(this.uiHandler);

        this.action = new SettingsAction();

        this.responseHandler = new SettingsResponseHandler(this.action);
        this.serverMapper.AddInstance(this.responseHandler);

        var defaultSettings = {
            Language: "",
            DefaultUnit: "",
            ActiveSite: "",
            SiteList: [],
            PharmacoTherapeuticActive: false,
            DefaultRefillNumber: 0,
            AreDiagnosticsInPatientSummary: false,
            UsefulUrl: "",
            IsAutoResponseActive: false,
            IsInVacation: false,
            FromVacationDate: null,
            ToVacationDate: null,
            VacationNote: "",
            IsPinActive: false,
            AreResultsShared: false,
            PrintLabelPortrait: false,
            PrintLabelLandscape: false,
            PrintHightWidth_TypeA: false,
            PrintHightWidth_TypeB: false,
            PrintPrescriptionRegular: false,
            PrintPrescriptionThermal: false,
            PrescriptionPrintType: 0,
            PrintConfirmationRegular: false,
            PrintConfirmationThermal: false,
            PDFSearchKeywords: [],
            SelectePDFTagIds: [],
            IsDSQSynchronized: false,
            LanguageOptions: [],
            UnitOptions: [],
            DocumentView: [],
            SignatureByteArray: "",
            CliniciansForTasks: [],
            CliniciansForMessages: [],
            CliniciansForExternalCommunications: [],
            CliniciansForPortalCommunications: [],
            ClinicianIdForOrphanLabs: "",
            ClinicianDateFromOrphanLabs: null,
            ClinicianDateToOrphanLabs: null,
            ClinicianListForOrphanLabs: [],
            CliniciansAsKVPList: [],
            SingOnPrint: false,
            FaxAccounts: [],
            PreferedFaxAccountNumberId: null,
            ShowDiagnosisCode: false,
            ActiveDepartment: "",
            ShowAlertsAndSpecialNeedsInPrintedSummary: false,
            ShowPopupForNewAlertsAndSpecialNeeds: false
        };

        defaultSettings.CliniciansAsKVPList = SettingsStore.getCliniciansAsKeyValuePairs();

        var defaultUIState = {
            ShowPwdChange: false,
            PwdConfirmationInProgress: false,
            EmailVerificationInProcess: false,
            ShowEditEmailAddresses: false,
            ShowUrlEdit: false,
            ShowPINChange: false,
            LoadingTagsCache: true
        };

        defaultSettings.Language = sessionStore.userLanguageCode;

        this.configuration = Immutable.fromJS(defaultSettings);

        this.uiLogic = Immutable.fromJS(defaultUIState);

        const langOptions = [
            new KeyValuePair("Français", "fr"),
            new KeyValuePair("English", "en"),
            new KeyValuePair("Español", "es-MX")
        ];

        this.ResetUnitOptions();

        this.resetDocumentView();

        let siteList: SitesModel[] = [];

        const currentUser = sessionStore.getCurrentEmployee().Model;
        const licenceOrganizationRule = Employee.getLicenceOrganisationRule(currentUser);

        let siteFinder = (id: string): SitesModel => cacheStore.SiteCache
            .filter((s: SitesModel) => s.Model.Id === id)[0];

        currentUser.AssociatedSites.forEach(x => {
            let site = siteFinder(x);
            if (!(site == null))
                siteList.push(site);
        });

        this.configuration = this.configuration.set("LanguageOptions", Immutable.List(langOptions));
        this.configuration = this.configuration.set("SiteList", siteList);
        this.configuration = this.configuration.set("ActiveSite", currentUser.ActiveSiteId);

        this.configuration = this.configuration.set("PharmacoTherapeuticActive", !currentUser.PharmacotherapeuticAssistantDisable);
        this.configuration = this.configuration.set("AreDiagnosticsInPatientSummary", currentUser.IsProblemVisibility);
        this.configuration = this.configuration.set("DefaultRefillNumber", currentUser.DefaultRefill);
        this.configuration = this.configuration.set("UsefulUrl", currentUser.UsefulLinks.toString());
        this.configuration = this.configuration.set("AreResultsShared", currentUser.IsSharingLabs);

        this.configuration = this.configuration.set("ShouldAttachMedicalSummaryWhenCreatingConsultationRequest", currentUser.ShouldAttachMedicalSummaryWhenCreatingConsultationRequest);
        this.configuration = this.configuration.set("ShouldAttachMedicationSummaryWhenCreatingConsultationRequest", currentUser.ShouldAttachMedicationSummaryWhenCreatingConsultationRequest);
        this.configuration = this.configuration.set("ShowAlertsAndSpecialNeedsInPrintedSummary", currentUser.ShowAlertsAndSpecialNeedsInPrintedSummary);
        this.configuration = this.configuration.set("ShowPopupForNewAlertsAndSpecialNeeds", currentUser.ShowPopupForNewAlertsAndSpecialNeeds);

        // auto-responder
        var vacationSet = currentUser.IsInVacation == null ? false : currentUser.IsInVacation;
        this.configuration = this.configuration.set("IsInVacation", vacationSet);
        this.configuration = this.configuration.set("FromVacationDate", currentUser.FromVacationDate);
        this.configuration = this.configuration.set("ToVacationDate", currentUser.ToVacationDate);
        this.configuration = this.configuration.set("VacationNote", currentUser.VacationNote);

        this.configuration = this.configuration.set("SignatureByteArray", currentUser.SignatureByteArray);

        // printing
        this.configuration = this.configuration.set("ArePrintLabelsPortrait", currentUser.PrintingSetting);
        this.configuration = this.configuration.set("IsLargePrint", currentUser.LargeLabelPrinting);
        this.configuration = this.configuration.set("IsNormalPrescriptionPrinter", currentUser.IsRegularPrinter);
        this.configuration = this.configuration.set("prescriptionPrintType", currentUser.PrescriptionPrintType);
        this.configuration = this.configuration.set("IsNormalConfirmationPrinter", currentUser.IsRegularRDVConfirmPrinter);

        // pdf
        var currentTagList = (currentUser.AllowedFormTags != null && currentUser.AllowedFormTags.length > 0) ? currentUser.AllowedFormTags[0].split("*") : null;
        this.configuration = this.configuration.set("PDFSearchKeywords", Immutable.List(currentTagList));
        this.configuration = this.configuration.set("FaxAccounts", Immutable.List(cacheStore.faxAccounts));
        this.configuration = this.configuration.set("PreferedFaxAccountNumberId", currentUser.PreferedFaxAccountNumberId);

        this.configuration = this.configuration.set("DefaultUnit", sessionStore.useMetricSystemAsDefault ? "metric" : "imperial");

        this.configuration = this.configuration.set("ShowDiagnosisCode", currentUser.ShowDiagnosisCode);

        this.configuration = this.configuration.set("EmailAddressesModel", EmailAddressesModel.getDefault());

        this.configuration = this.configuration.set(
            "ShouldShowAndCalculateMedicationDispensationQuantities",
            currentUser.ShouldShowAndCalculateMedicationDispensationQuantities
            ?? (licenceOrganizationRule?.StateCode === "BC" && licenceOrganizationRule?.CountryCode === "CA")
        );

        if (cacheStore.ClinicSettings?.EnableQuickDocumentSigning) {
            let currentUserLoginPinFrequency = LoginPinFrequencyModel.fromResponseModel(currentUser.LoginPinFrequency);
            currentUserLoginPinFrequency = this.getCurrentUserPinFrequency(currentUserLoginPinFrequency);

            this.configuration = this.configuration.set("LoginPinFrequency", currentUserLoginPinFrequency);
        }

        this.configuration = this.configuration.set(
            "LongTermMedicationDurationInDays",
            currentUser.LongTermMedicationDurationInDays
        );

        this.configuration = this.configuration.set(
            "LongTermMedicationDefaultRefillNumber",
            currentUser.LongTermMedicationDefaultRefillNumber
        );

        this.configuration = this.configuration.set("SelectedDrugInteractionAlertsFilterOption", currentUser.DrugInteractionAlertsFilterOption);

        this.ClientDispatchToken = LocalEventDispatcher.register((request: LocalEvent) => { self.mapper.Map(request, self); });
        this.ServerDispatchToken = ServerResponseDispatcher.register((response: ServerEvent) => { self.serverMapper.Map(response, self); });

        this.configuration = this.configuration
            .set("ActiveDepartment", currentUser.ActiveDepartmentId)
            .set("CostCentersList", cacheStore.getCostCenterBySiteId(currentUser.ActiveSiteId))
            .set("IsTelemedWindowInMyleWindow", currentUser.ShowTelemedWindowInMylePopup)
            .set("SelectedAuthenticatorCodeDeliveryMethod", sessionStore.AuthenticatorCodeDeliveryMethod)
            .set("AllowPatientsToInitiatePortalMessages", currentUser.AllowPatientsToInitiatePortalMessages);
    }

    public static createSingleton(): SettingsStore {
        return this.createStoreSingleton(defaultInstance, () => defaultInstance = new SettingsStore());
    }

    public static createSingletonFrom(store: SettingsStore, copy: boolean = true): SettingsStore {
        return copy
            ? Object.assign(this.createSingleton(), store)
            : this.createStoreSingleton(defaultInstance, () => defaultInstance = store);
    }

    public ResetUnitOptions(): void {
        const unitOptions = [
            new KeyValuePair(cacheStore.GetString("MetricUnitsLabel").toTitleCase(), "metric"),
            new KeyValuePair(cacheStore.GetString("ImperialUnitsLabel").toTitleCase(), "imperial")
        ];
        this.configuration = this.configuration.set("UnitOptions", Immutable.List(unitOptions));
    }

    public resetDocumentView(): void {
        const documentViewOptions = [
            new KeyValuePair(cacheStore.GetString("DocumentAnnotation_AnnotateDocumentViewLabel"), "annotateView"),
            new KeyValuePair(cacheStore.GetString("DocumentAnnotation_PopoutDocumentViewLabel"), "popoutView")
        ];
        this.configuration = this.configuration.set("DocumentView", Immutable.List(documentViewOptions));
    }

    private static getCliniciansAsKeyValuePairs(): KeyValuePair<Guid, string>[] {
        return cacheStore
            .GetActiveEmployees()
            .sortByProperty(employee => employee.LastName)
            .map(employee => {
                const person = Person.FromEmployee(employee);
                return person.Licence
                    ? new KeyValuePair(person.ContactId, person.Name)
                    : null;
            })
            .filter(pair => pair != null);
    }

    /**
     * Get patient pin frequency model
     * @param currentPinFrequency : LoginPinFrequencyModel
     * @returns LoginPinFrequencyModel
     */
    private getCurrentUserPinFrequency(currentPinFrequency: LoginPinFrequencyModel): LoginPinFrequencyModel {
        if (!currentPinFrequency.prescriptionFrequency)
            currentPinFrequency.prescriptionFrequency = PinFrequency.Always;

        if (!currentPinFrequency.documentFrequency)
            currentPinFrequency.documentFrequency = PinFrequency.Always;

        if (!currentPinFrequency.formFrequency)
            currentPinFrequency.formFrequency = PinFrequency.Always;

        if (!currentPinFrequency.consultationRequestFrequency)
            currentPinFrequency.consultationRequestFrequency = PinFrequency.Always;

        if (!currentPinFrequency.examRequestFrequency)
            currentPinFrequency.examRequestFrequency = PinFrequency.Always;

        if (!currentPinFrequency.obstetricalFileFrequency)
            currentPinFrequency.obstetricalFileFrequency = PinFrequency.Always;

        return currentPinFrequency;
    }
}

export { SettingsStore, defaultInstance as settingsStore };
